// Pure audit text helpers, shared by the table cells and the kore adapter.
// Kept apart from both so neither imports the other.

import type { AuditDetails } from "@pej/kore/types";
import { countOf } from "@/lib/plural";

/** PascalCase wire enum → spaced phrase (`DesignTemplate` → `Design Template`). */
export function formatTargetType(type: string): string {
    return type.replace(/([a-z])([A-Z])/g, "$1 $2");
}

function fieldCount(n: number, verb: string): string {
    return `${countOf(n, "field")} ${verb}`;
}

function isCount(value: unknown): value is number {
    return typeof value === "number" && Number.isFinite(value);
}

/**
 * One-line summary for the Details cell. kore's `AuditDetails` is open
 * (`{ type: string; [key: string]: unknown }`), so each branch checks the
 * payload shape; anything unrecognised reads as "—".
 */
export function summarizeDetails(details: AuditDetails): string {
    if (details.type === "Created" && Array.isArray(details.fields)) {
        return fieldCount(details.fields.length, "set");
    }
    if (details.type === "Updated" && Array.isArray(details.changes)) {
        return fieldCount(details.changes.length, "changed");
    }
    if (details.type === "Deleted" && typeof details.reason === "string" && details.reason.length > 0) {
        return details.reason;
    }
    if (details.type === "PlacementTokenRotated") {
        return "New link generated";
    }
    if (details.type === "InvitesCreated" && isCount(details.count)) {
        return `${countOf(details.count, "invite")} created`;
    }
    if (details.type === "RespondentsErased" && isCount(details.respondents) && isCount(details.responses)) {
        return `Erased ${countOf(details.responses, "response")} from ${countOf(details.respondents, "respondent")}`;
    }
    return "—";
}
